/**
 * <TranscriptBubble>
 *
 * One speaker-labeled chat-style message in the live-transcript stream
 * (Phase 2 item 2.10 of the AI Scribe rollout). Renders an optional small
 * speaker label above the text plus the caption body.
 *
 * Variants
 *  - `light` (default) — historical light-card visual: grey card on white
 *    background, monospace body in `#313131`. Used by the legacy panel
 *    layout and any non-Zoom transcript surfaces.
 *  - `dark` — the §7 "Live transcript" variant from the new ZoomPanel
 *    redesign. Speakers are colored: PROVIDER → light-blue `#93c5fd`,
 *    PATIENT → light-green `#86efac`. Body line-height 1.6 in `#cbd5e1`.
 *    The bubble itself is transparent so the dark slate panel shows through.
 *
 * Speaker normalization
 *  - The hook (`useLiveTranscription`) emits the Zoom user's `displayName` for
 *    live captions (and `'doctor' | 'patient'` for server-seeded recovery
 *    lines). The `dark` variant maps those values to PROVIDER / PATIENT for the
 *    badge so the diarization labels stay consistent regardless of source.
 */
import type { ReactNode } from 'react'
import { Badge } from '@mantine/core'
import { cn } from '@/utils/cn'
import { scribeText } from './scribeText'

export type TranscriptBubbleVariant = 'light' | 'dark'

export type TranscriptBubbleProps = {
  /** Optional speaker label (e.g. "Dr. Smith", "patient"). Hidden when omitted. */
  speaker?: string
  /** Caption body. Required — empty captions are filtered upstream in `useLiveTranscription`. */
  text: string
  /** ISO-8601 timestamp the caption was captured at. The dark variant formats
   *  this to `h:mm A` via `formatBubbleTime`. The light variant ignores it and
   *  uses `displayTime` instead (the persisted accordion source has already
   *  done the formatting, so we accept the pre-formatted string verbatim). */
  timestamp?: string
  /** Pre-formatted clock time (e.g. `2:54 PM`) for the light variant. Used by
   *  the persisted-transcript accordion which parses the BE's `[h:mm A]`
   *  prefix and passes the display string through. */
  displayTime?: string
  /** Explicit speaker role. When provided, takes precedence over the
   *  heuristic-based `normalizeSpeakerRole(speaker)` derivation. Callers
   *  that swap `speaker` to a canonical FHIR name (e.g. the persisted-
   *  transcript accordion) MUST pass `role` so the bubble doesn't misclassify
   *  e.g. "Noah Kim" as a provider just because the name doesn't contain
   *  the substring "patient". */
  role?: 'provider' | 'patient' | 'coordinator'
  /** Visual variant. Defaults to `light` for backwards compat with the existing TranscriptStream. */
  variant?: TranscriptBubbleVariant
  /**
   * Optional override for additional content (e.g. allergy badges) that
   * future phases want to render inside the bubble. Kept as a slot so the
   * component contract stays narrow.
   */
  children?: ReactNode
}

/**
 * Map raw speaker values onto a normalized role so both variants color the
 * speaker label consistently. Live + server-seeded lines carry `doctor` /
 * `patient` / `care coordinator`. "patient" → patient, "coordinator"/"manager"
 * → coordinator, everything else → provider (so a raw Zoom name like
 * "Dr. Smith" still reads as the provider).
 */
export function normalizeSpeakerRole(
  raw?: string,
): 'provider' | 'patient' | 'coordinator' | undefined {
  if (!raw) return undefined
  const lower = raw.toLowerCase()
  if (lower.includes('patient')) return 'patient'
  if (lower.includes('coordinator') || lower.includes('manager')) return 'coordinator'
  return 'provider'
}

/**
 * Color tokens per role. Dark variant uses light-bg-readable hues against
 * the slate-900 panel; light variant uses darker hues against the white
 * card so the contrast meets WCAG AA at 12-14px.
 */
const ROLE_COLORS = {
  dark: {
    provider: '#93c5fd', // light blue
    patient: '#86efac', // light green
    coordinator: '#fcd34d', // amber-300
  },
  light: {
    provider: '#1d4ed8', // blue-700 — readable on the #F3F5F8 card
    patient: '#15803d', // green-700 — same contrast tier
    coordinator: '#b45309', // amber-700 — same contrast tier
  },
} as const

// Speaker-badge text per variant, keyed by role.
const ROLE_LABELS = {
  dark: { provider: 'PROVIDER', patient: 'PATIENT', coordinator: 'COORDINATOR' },
  light: { provider: 'Doctor', patient: 'Patient', coordinator: 'Coordinator' },
} as const

const ROLE_BADGE_COLORS = { provider: 'blue', patient: 'green', coordinator: 'yellow' } as const

/**
 * Compact `h:mm A` clock-time for a per-row stamp (e.g. "3:42 PM"). Returns
 * an empty string when the ISO timestamp is missing or unparseable so the
 * bubble degrades silently instead of rendering "Invalid Date".
 */
function formatBubbleTime(timestamp?: string): string {
  if (!timestamp) return ''
  const ms = Date.parse(timestamp)
  if (Number.isNaN(ms)) return ''
  return new Date(ms).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export function TranscriptBubble({
  speaker,
  text,
  timestamp,
  displayTime,
  role: explicitRole,
  variant = 'light',
  children,
}: TranscriptBubbleProps) {
  // Use the explicit role when provided (the persisted-transcript path swaps
  // the speaker to a canonical FHIR name and passes the role separately).
  // Fall back to heuristic detection on the speaker string for the live
  // transcript path where `speaker` is still the raw Zoom display name.
  const role = explicitRole ?? normalizeSpeakerRole(speaker)

  if (variant === 'dark') {
    const label = role ? ROLE_LABELS.dark[role] : undefined
    const labelColor = role ? ROLE_COLORS.dark[role] : '#93c5fd'
    const formattedTime = formatBubbleTime(timestamp)
    return (
      <div
        data-testid="transcript-bubble"
        data-speaker={speaker ?? ''}
        data-variant="dark"
        data-timestamp={timestamp ?? ''}
        className="mb-3"
      >
        {(label || formattedTime) && (
          <div className="mb-[2px] flex items-baseline justify-between gap-2">
            {label ? (
              <div
                data-testid="transcript-bubble-speaker"
                className={scribeText.eyebrow}
                style={{ color: labelColor }}
              >
                {label}
              </div>
            ) : (
              <span />
            )}
            {formattedTime && (
              <div
                data-testid="transcript-bubble-time"
                className={`${scribeText.micro} tracking-[0.03em] text-slate-600 tabular-nums`}
              >
                {formattedTime}
              </div>
            )}
          </div>
        )}
        <div
          data-testid="transcript-bubble-text"
          className={`${scribeText.body} leading-relaxed break-words whitespace-pre-wrap text-slate-300`}
        >
          {text}
        </div>
        {children}
      </div>
    )
  }

  // Role pill carries the speaker identity (Doctor / Patient); the name
  // itself renders in a neutral color so we don't double-encode role via
  // both pill color AND name color. Falls back to a muted gray when we
  // can't classify the speaker. `role` was resolved above using the
  // explicit prop with a fallback to the speaker-string heuristic.
  const roleLabel = role ? ROLE_LABELS.light[role] : null
  const roleBadgeColor = ROLE_BADGE_COLORS[role ?? 'provider']

  return (
    <div
      data-testid="transcript-bubble"
      data-speaker={speaker ?? ''}
      data-role={role ?? ''}
      data-variant="light"
      data-timestamp={timestamp ?? ''}
      data-display-time={displayTime ?? ''}
      className={cn('mb-2 rounded-md bg-[#F3F5F8] px-3 py-2')}
    >
      {(speaker || displayTime) && (
        <div className="mb-1 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {roleLabel && (
              <Badge
                size="xs"
                variant="light"
                color={roleBadgeColor}
                data-testid="transcript-bubble-role"
              >
                {roleLabel}
              </Badge>
            )}
            {speaker && (
              <div
                data-testid="transcript-bubble-speaker"
                className="truncate text-xs font-semibold tracking-wide text-[#374151]"
              >
                {speaker}
              </div>
            )}
          </div>
          {displayTime && (
            <div
              data-testid="transcript-bubble-time"
              className="font-mono text-[10px] tracking-[0.03em] text-[#9aa0a6] tabular-nums"
            >
              {displayTime}
            </div>
          )}
        </div>
      )}
      <div
        data-testid="transcript-bubble-text"
        className="font-mono text-sm leading-relaxed break-words whitespace-pre-wrap text-[#313131]"
      >
        {text}
      </div>
      {children}
    </div>
  )
}
